import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useAnimationFrame, wrap } from "framer-motion";
import { Images } from "lucide-react";

const IMAGES = [
  "/carousel-1.jpg",
  "/carousel-2.jpg",
  "/carousel-3.jpg",
  "/carousel-4.jpg",
  "/carousel-5.jpg",
  "/carousel-6.jpg",
  "/carousel-7.jpg",
  "/carousel-8.jpg",
  "/carousel-9.jpg",
];

const SPEED = 60;

export default function Marquee() {
  const x = useMotionValue(0);
  const trackRef = useRef(null);
  const half = useRef(0);
  const [paused, setPaused] = useState(false);
  const dragging = useRef(false);

  useEffect(() => {
    const measure = () => {
      if (trackRef.current) half.current = trackRef.current.scrollWidth / 2;
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  useAnimationFrame((_, delta) => {
    if (!half.current) return;
    let next = x.get();
    if (!paused && !dragging.current) next -= (SPEED * delta) / 1000;
    x.set(wrap(-half.current, 0, next));
  });

  const track = [...IMAGES, ...IMAGES];
  return (
    <section
      data-testid="marquee-section"
      className="marquee-section relative w-full overflow-hidden border-y border-white/5 bg-[#08080c] py-16 md:py-24"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="mx-auto mb-10 flex max-w-7xl flex-col items-center gap-3 px-6 lg:px-10">
        <span
          data-testid="marquee-tag"
          className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] py-1.5 pl-1.5 pr-4 backdrop-blur-sm"
        >
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-[#3ea6ff] to-[#e0359e]">
            <Images className="h-3.5 w-3.5 text-white" />
          </span>
          <span className="text-sm font-medium text-white/80">Deployed in the Field</span>
        </span>
      </div>

      <div className="relative">
        <motion.div
          ref={trackRef}
          data-testid="marquee-track"
          className="flex w-max cursor-grab touch-pan-y active:cursor-grabbing"
          style={{ x }}
          drag="x"
          onDragStart={() => {
            dragging.current = true;
          }}
          onDragEnd={() => {
            dragging.current = false;
          }}
        >
          {track.map((src, i) => (
            <img
              key={i}
              src={src}
              alt={`Dolphin deployment ${(i % IMAGES.length) + 1}`}
              loading="lazy"
              draggable={false}
              data-testid={`marquee-image-${i + 1}`}
              className="mx-4 h-64 w-auto select-none rounded-2xl border border-white/10 object-cover opacity-80 transition-opacity duration-500 hover:opacity-100 md:h-96"
            />
          ))}
        </motion.div>
        <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-[#08080c] to-transparent md:w-40" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-[#08080c] to-transparent md:w-40" />
      </div>
    </section>
  );
}
